import React from 'react'
import { PersonLikeData, getPersonInitials } from '../utils/people'

interface PersonAvatarProps {
  data: PersonLikeData | null | undefined
  /** Tailwind size class pair (`h-* w-*`) controls the bounding box; the
   *  initials fallback adapts via `text-*` set by the caller. Defaults match
   *  the chip-strip sizing. */
  className?: string
  /** Tailwind text-size class for the initials fallback. */
  initialsClassName?: string
  alt?: string
}

/**
 * Square avatar for Persons. Renders the `picture` URL when present, otherwise
 * a coloured circle with initials. The colour is hashed off the initials so
 * the same person looks the same across surfaces.
 */
const PersonAvatar: React.FC<PersonAvatarProps> = ({
  data,
  className = 'h-5 w-5',
  initialsClassName = 'text-[9px]',
  alt,
}) => {
  const picture = typeof data?.picture === 'string' ? data.picture.trim() : ''
  const initials = getPersonInitials(data)

  if (picture) {
    return (
      <img
        src={picture}
        alt={alt || initials}
        className={`${className} rounded-full object-cover bg-gray-200`}
      />
    )
  }

  const palette = [
    'bg-rose-200 text-rose-800',
    'bg-amber-200 text-amber-800',
    'bg-lime-200 text-lime-800',
    'bg-emerald-200 text-emerald-800',
    'bg-sky-200 text-sky-800',
    'bg-violet-200 text-violet-800',
    'bg-fuchsia-200 text-fuchsia-800',
  ]
  const hash = Array.from(initials).reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  const colour = palette[hash % palette.length]

  return (
    <span
      className={`${className} ${colour} rounded-full inline-flex items-center justify-center font-semibold ${initialsClassName}`}
      aria-label={alt || initials}
    >
      {initials}
    </span>
  )
}

export default PersonAvatar
